import React, { useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/mevo/ic-exit-rounded-purple.svg';
import { ReactComponent as LeftArrowIcon } from '../../assets/images/icons/mevo/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/images/icons/right-arrow.svg';
import { useIsMobile } from '../../helpers/useIsMobile';
import { validateEmail, validatePhone } from '../../helpers/validations';
import { Button } from '../UI-CHAKRA/Button';
import { Input } from '../UI-CHAKRA/Input/Input';
import { MaskedInput, MASKS } from '../UI-CHAKRA/Input/MaskedInput';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { FormErrorMessage } from '../UI-CHAKRA/FormErrorMessage';

interface PbmReviewStepProps {
  email: string;
  phone: string;
  isLoading: boolean;
  controllerName?: string;
  onEmailChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onBack: () => void;
  onConfirm: () => void;
  onClose: () => void;
  onEventEmission: (eventName: string, eventData: Record<string, unknown>) => void;
}

const PbmReviewStep: React.FC<PbmReviewStepProps> = ({
  email,
  phone,
  isLoading,
  controllerName = '',
  onEmailChange,
  onPhoneChange,
  onBack,
  onConfirm,
  onClose,
  onEventEmission,
}) => {
  const isMobile = useIsMobile();
  const isEmailInvalid = email.length > 0 && !validateEmail(email);
  const isPhoneInvalid = phone.length > 0 && !validatePhone(phone);
  const isReviewConfirmDisabled = !email || !phone || isEmailInvalid || isPhoneInvalid;
  const hasEmittedViewEventRef = useRef(false);
  const displayControllerName = controllerName || '[Controladora]';

  //por enquanto vai funcionar só pra telefones no padrão brasileiro
  const phoneWithoutCountryCode = phone?.startsWith('+55') ? phone.replace('+55', '') : phone;
  useEffect(() => {
    if (hasEmittedViewEventRef.current) return;
    hasEmittedViewEventRef.current = true;
    onEventEmission('backoffice_campaign_pbm_contato_review_view', {
      flow: 'contato paciente',
    });
  }, [onEventEmission]);

  return (
    <Flex flexDirection={'column'} height={'100%'} overflow="hidden">
      <Box flexShrink={0} borderBottom={'1px solid #E9ECEF'}>
        {isMobile ? (
          <Flex flexDirection={'row'} alignItems={'center'} gap={'8px'} padding={'20px'}>
            <LeftArrowIcon onClick={onBack} cursor={'pointer'} />
            <Text fontSize={'14px'} fontWeight={700} color={'primary.main'}>
              Voltar
            </Text>
          </Flex>
        ) : (
          <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} padding={'20px'}>
            <Text fontSize={'18px'} fontWeight={700} color={'#343A40'}>
              Programa de Benefício em Medicamentos
            </Text>
            <CloseIcon onClick={onClose} cursor={'pointer'} />
          </Flex>
        )}
      </Box>

      <Box flexGrow={1} overflowY={'auto'} overflowX="hidden" padding={'20px'}>
        <Flex flexDirection={'column'}>
          <Text fontSize={'18px'} fontWeight={700} color={'primary.main'}>
            Revise os dados do paciente
          </Text>

          <Text fontSize={'14px'} color={'#343A40'} lineHeight={'17px'} paddingBottom={'18px'}>
            Para concluir o cadastro, precisamos do telefone e do e-mail atualizados do paciente. São os dados mínimos
            necessários para a {displayControllerName} concluir o cadastro no programa e informar o paciente sobre o
            benefício.
          </Text>

          <Flex flexDirection={'column'} gap={'20px'}>
            <FormControl isInvalid={isEmailInvalid}>
              <FormLabel fontSize={'12px'} fontWeight={700} color={'primary.main'} mb={'8px'}>
                E-MAIL DO PACIENTE*
              </FormLabel>
              <Input
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="name@email.com.br"
                variant="outline"
                type="email"
              />
              <FormErrorMessage>Informe um e-mail válido</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={isPhoneInvalid}>
              <FormLabel fontSize={'12px'} fontWeight={700} color={'primary.main'} mb={'8px'}>
                TELEFONE DO PACIENTE*
              </FormLabel>
              <MaskedInput
                mask={MASKS.BRAZIL_PHONE}
                value={phoneWithoutCountryCode}
                onChange={(e) => onPhoneChange(e.target.value)}
                placeholder={'11 99999-0000'}
                variant="outline"
              />
              <FormErrorMessage>Informe um telefone válido</FormErrorMessage>
            </FormControl>
          </Flex>
        </Flex>
      </Box>

      <Box flexShrink={0} padding={'20px'} borderTop={'1px solid #E9ECEF'}>
        <Flex flexDirection={'row'} gap={'12px'} justifyContent={'flex-end'}>
          <Button
            onClick={onBack}
            padding="10px 20px"
            backgroundColor={'#FFF'}
            borderWidth={'2px'}
            borderColor={'primary.main'}
            borderStyle={'solid'}
            borderRadius={'20px'}
            _hover={{ backgroundColor: '#FFF', borderColor: '#6B409C' }}
          >
            <Text fontSize={'14px'} color={'primary.main'}>
              Voltar
            </Text>
          </Button>
          <Button
            onClick={onConfirm}
            isDisabled={isReviewConfirmDisabled || isLoading}
            isLoading={isLoading}
            padding="10px 20px"
            backgroundColor={'primary.main'}
            borderRadius={'20px'}
            gap={'8px'}
            color={'#FFF'}
            fontSize={'14px'}
            _hover={{ backgroundColor: '#6B409C' }}
            _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
            {...(!isLoading && { rightIcon: <RightArrowIcon width={'16px'} height={'16px'} fill="#FFF" /> })}
          >
            {isLoading ? 'Processando...' : 'Confirmar'}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
};

export default PbmReviewStep;
